import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { action, computed, makeObservable, observable } from "mobx";

export class BulkActionsStore {
  @observable.ref
  actions: BulkAction[];

  @observable
  selectedIds = new Set<string>();

  @observable.ref
  allIds: string[] = [];

  constructor(
    actions: BulkAction[],
    allIds?: string[],
    private onSelectionChange?: (selectedIds: string[]) => void,
    private onAfterSelectAll?: (allIds: string[]) => void,
  ) {
    makeObservable(this);
    this.allIds = allIds ?? [];
    this.actions = actions;
  }

  isSelected(id: string) {
    return this.selectedIds.has(id);
  }

  isIndeterminate(id: string, subtaskIds: string[]) {
    return (
      this.isSelected(id) && !subtaskIds.every((id) => this.isSelected(id))
    );
  }

  @computed
  get selectedCount() {
    return this.selectedIds.size;
  }

  @computed
  get isSelectAllIndeterminate() {
    return (
      this.selectedIds.size > 0 && this.selectedIds.size < this.allIds.length
    );
  }

  @computed
  get isSelectAllChecked() {
    if (this.selectedCount === 0) return false;
    return this.selectedCount === this.allIds.length;
  }

  @computed
  get selectedIdsArray() {
    return Array.from(this.selectedIds);
  }

  @computed
  get showSelectAllButton(): boolean {
    if (this.allIds.length === 0) return false;
    return this.selectedCount !== this.allIds.length;
  }

  @action.bound
  setActions(actions: BulkAction[]) {
    this.actions = actions;
  }

  @action.bound
  setAllIds(allIds: string[]) {
    if (
      this.allIds.length === allIds.length &&
      this.allIds.every((id, index) => id === allIds[index])
    ) {
      return;
    }
    this.allIds = allIds;
  }

  @action.bound
  setSelectedIds(ids: string[] | Set<string>) {
    const nextIdsSet = new Set(ids);

    if (
      this.selectedIds.size === nextIdsSet.size &&
      Array.from(nextIdsSet).every((id) => this.selectedIds.has(id))
    ) {
      return;
    }

    this.selectedIds = nextIdsSet;
  }

  notifySelectionChange() {
    this.onSelectionChange?.(this.selectedIdsArray);
  }

  @action.bound
  handleSelect(id: string, subtaskIds: string[]) {
    if (subtaskIds.includes(id)) {
      this.isSelected(id)
        ? this.selectedIds.delete(id)
        : this.selectedIds.add(id);
      this.notifySelectionChange();
      return;
    }

    if (this.isIndeterminate(id, subtaskIds)) {
      for (const subtaskId of subtaskIds) {
        this.selectedIds.add(subtaskId);
      }
      this.notifySelectionChange();
      return;
    }

    if (this.isSelected(id)) {
      this.selectedIds.delete(id);
      for (const subtaskId of subtaskIds) {
        this.selectedIds.delete(subtaskId);
      }
    } else {
      this.selectedIds.add(id);
    }

    this.notifySelectionChange();
  }

  @action.bound
  handleSelectAll() {
    this.selectedIds = new Set(this.allIds);
    this.notifySelectionChange();
    this.onAfterSelectAll?.(this.allIds);
  }

  @action.bound
  handleCancelSelection() {
    this.selectedIds.clear();
    this.notifySelectionChange();
  }

  handleToggleAll() {
    if (!this.isSelectAllChecked) {
      this.handleSelectAll();
      return;
    }
    this.handleCancelSelection();
  }
}
